import type { UserResource } from "@octopusdeploy/octopus-server-client";
import { semanticConventions } from "@octopusdeploy/telemetry";
import type { Span, SpanProcessor } from "@opentelemetry/sdk-trace-web";
import { anonymizeString } from "~/analytics/anonymizeString";
import { client, session } from "~/clientInstance";
import { browserSessionId } from "~/openTelemetry/context/browserSessionId";
import { currentPageContext } from "~/openTelemetry/context/currentPageContext";
import { currentThemeContext } from "~/openTelemetry/context/currentThemeContext";
import { pageInstanceTelemetryAttributes } from "~/openTelemetry/pageInstanceTelemetryAttributes";
export class PortalAttributesSpanProcessor implements SpanProcessor {
    onStart(span: Span) {
        span.setAttributes({
            "browser.session.id": browserSessionId,
            "frontend.user.id": session.currentUser?.Id,
            "frontend.user.globally_unique_id": session.currentUser ? getGloballyUniqueUserId(session.currentUser) : undefined,
            "frontend.feature_toggles.enabled": session.featureToggles?.filter((t) => t.IsEnabled).map((t) => t.Name) ?? [],
            theme: currentThemeContext.getCurrentThemePaletteType(),
            "theme.selection": currentThemeContext.getCurrentTheme() === "unassigned" ? "system" : currentThemeContext.getCurrentTheme(),
        });
        // If we already have the page attributes on a span, do nothing.
        // We don't want to override these attributes because the span might be associated with a
        // previous page that is no longer on the current page context
        // This is only likely to occur in async code paths
        const hasPageAttributes = span.attributes[semanticConventions.pageId] !== undefined && span.attributes[semanticConventions.pageInstanceId] !== undefined;
        if (hasPageAttributes) {
            return;
        }
        const currentPage = currentPageContext.getCurrentPage();
        if (currentPage !== null) {
            const { routeTemplate, routeParams, Id, instanceId, owner } = currentPage;
            span.setAttributes(pageInstanceTelemetryAttributes(Id, instanceId, routeTemplate, routeParams, owner));
        }
    }
    onEnd() { }
    forceFlush() {
        return Promise.resolve();
    }
    shutdown() {
        return Promise.resolve();
    }
}
function getGloballyUniqueUserId(user: UserResource): string {
    // This aligns with the backend's implementation for generating user ids for amplitude,
    // allowing us to find the same users in both honeycomb and amplitude
    return anonymizeString(getPlainTextGloballyUniqueUserId(user));
}
function getPlainTextGloballyUniqueUserId(user: UserResource) {
    if (user.EmailAddress) {
        return user.EmailAddress;
    }
    const installationId = client.tryGetServerInformation()?.installationId;
    if (!installationId) {
        // This should never happen, because we should have a connected client well before we get a user
        return user.Id;
    }
    return `${installationId}-${user.Id}`;
}
